/**
 * F-148 translated error mapping (design §2.2, §9.2, §12).
 *
 * The shared service throws `StockReceivingError { code, details, retryable }`. This module
 * duck-types on that shape (no class import) so components and tests can pass plain objects.
 * Every mapping returns a `TranslationKey`; raw server text is never shown.
 */
import type {
  ReceivingCsvFileErrorCode,
  ReceivingCsvRowErrorCode,
  ReceivingDraftIssueCode,
  ReceivingLineIssueCode,
  StockReceivingErrorCode,
} from '@dukanti/shared/admin';
import type { TranslationKey } from '@dukanti/shared';

/**
 * i18next escapes interpolated values by default (`escapeValue: true`). Merchant content
 * (product names, supplier names, references) must render as authored, so templates that embed
 * it pass these options. React renders the result as text, never as HTML.
 */
export function withRawValues(values: Record<string, unknown>): Record<string, unknown> {
  return { ...values, interpolation: { escapeValue: false } };
}

export const STOCK_RECEIVING_ERROR_CODES: readonly StockReceivingErrorCode[] = [
  'F148_NOT_FOUND',
  'F148_DISABLED',
  'F148_VALIDATION',
  'F148_DRAFT_EXISTS',
  'F148_DRAFT_VERSION_CONFLICT',
  'F148_DRAFT_NOT_OPEN',
  'F148_REVIEW_STALE',
  'F148_DRAFT_INVALID',
  'F148_REFERENCE_ALREADY_RECEIVED',
  'F148_IDEMPOTENCY_KEY_REUSED',
  'F148_VARIANT_INELIGIBLE',
  'F148_INVENTORY_LIMIT',
  'F148_STOCK_BUSY',
  'F148_CORRECTION_EXCEEDS_RECEIPT',
  'F148_CORRECTION_EXCEEDS_AVAILABLE',
  'F148_UNKNOWN',
];

const CODE_SET: ReadonlySet<string> = new Set(STOCK_RECEIVING_ERROR_CODES);

export interface StockReceivingErrorInfo {
  code: StockReceivingErrorCode;
  details: Record<string, unknown>;
  retryable: boolean;
}

export function isStockReceivingErrorCode(value: unknown): value is StockReceivingErrorCode {
  return typeof value === 'string' && CODE_SET.has(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrows an unknown thrown value to the F-148 error shape, or null when it is something else. */
export function getStockReceivingErrorInfo(err: unknown): StockReceivingErrorInfo | null {
  if (!isRecord(err)) return null;
  const code = err.code;
  if (!isStockReceivingErrorCode(code)) return null;
  const details = isRecord(err.details) ? err.details : {};
  return { code, details, retryable: err.retryable === true };
}

function isTransportFailure(err: unknown): boolean {
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) return true;
  if (isRecord(err) && typeof err.message === 'string' && /Failed to fetch/i.test(err.message)) {
    return true;
  }
  return false;
}

/** Translation key for a thrown error. Unknown shapes map to network or unknown messages. */
export function receivingErrorKey(err: unknown): TranslationKey {
  const info = getStockReceivingErrorInfo(err);
  if (info) return `admin.receiving.errors.${info.code}`;
  if (isTransportFailure(err)) return 'common.errors.networkFailed';
  return 'admin.receiving.errors.F148_UNKNOWN';
}

/** Interpolation values that some error messages use (e.g. `currentVersion`). */
export function receivingErrorOptions(err: unknown): Record<string, unknown> {
  const info = getStockReceivingErrorInfo(err);
  return info ? info.details : {};
}

/** True when the hooks/UI may retry with the same key or token (§2.2). */
export function isRetryableReceivingError(err: unknown): boolean {
  const info = getStockReceivingErrorInfo(err);
  if (info) return info.retryable || info.code === 'F148_STOCK_BUSY';
  return isTransportFailure(err);
}

export function isVersionConflictError(err: unknown): boolean {
  return getStockReceivingErrorInfo(err)?.code === 'F148_DRAFT_VERSION_CONFLICT';
}

/** `currentVersion` from a version-conflict error, when the server supplied it. */
export function conflictCurrentVersion(err: unknown): number | null {
  const info = getStockReceivingErrorInfo(err);
  if (!info || info.code !== 'F148_DRAFT_VERSION_CONFLICT') return null;
  const value = info.details.currentVersion;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function issueMessageKey(
  code: ReceivingLineIssueCode | ReceivingDraftIssueCode,
): TranslationKey {
  return `admin.receiving.issues.${code}`;
}

export function csvErrorMessageKey(
  code: ReceivingCsvFileErrorCode | ReceivingCsvRowErrorCode,
): TranslationKey {
  return `admin.receiving.csv.errors.${code}`;
}

/** Template-column → line label key, for CSV row errors and field-level messages. */
export function csvColumnLabelKey(column: string | null): TranslationKey | null {
  switch (column) {
    case 'barcode':
      return 'admin.receiving.line.barcode';
    case 'dukanti_variant_id':
      return 'admin.receiving.line.variantRef';
    case 'supplier_code':
      return 'admin.receiving.line.supplierCode';
    case 'supplier_item_name':
      return 'admin.receiving.line.supplierItem';
    case 'unit_kind':
      return 'admin.receiving.line.unitKind';
    case 'packs':
      return 'admin.receiving.line.packs';
    case 'units_per_pack':
      return 'admin.receiving.line.unitsPerPack';
    case 'expected_units':
      return 'admin.receiving.line.expected';
    case 'accepted_units':
      return 'admin.receiving.line.accepted';
    case 'damaged_units':
      return 'admin.receiving.line.damaged';
    case 'missing_units':
      return 'admin.receiving.line.missing';
    case 'note':
      return 'admin.receiving.line.note';
    default:
      return null;
  }
}
